import {
  addWholeSlideImageToProject,
  deleteWholeSlideImage,
  getSlides,
  getSlidesToAnnotate,
  updateSlideStatus
} from "@/api/craiyon";
import {
  countNumberOfAnnotationsForSlide,
  fetchResourceWithIdentifier,
  prepareImagingStudy
} from "@/api/fhir";
import { identifierToString } from "@/utils/fhir-utils";
import Vue from "vue";
import { normalize, Slide, WSIStatus } from "@/store/helper";

const _TOTAL = "TOTAL";
const _LOAD_SLIDES = "LOAD_SLIDES";
const _SLIDES = "SLIDES";
const _SET_SLIDES = "SET_SLIDES";
const _LOADING = "LOADING";
const _ADD_SLIDE = "ADD_SLIDE";
const _DELETE_SLIDE = "DELETE_SLIDE";
const _REMOVE_SLIDE = "REMOVE_SLIDE";
const _SET_RAW_SLIDES = "SET_RAW_SLIDES";
const _TOGGLE_LOADING = "_TOGGLE_LOADING";
const _RESET_SLIDES = "_RESET_SLIDES";
const _ADD_SLIDE_TO_PROJECT = "ADD_SLIDE_TO_PROJECT";
const _FIND = "FIND";
const MODULE = "slides/";
const _LIST = "LIST";
const _UPDATE_SLIDE_STATUS = "UPDATE_SLIDE_STATUS";

export const UPDATE_SLIDE_STATUS = MODULE + _UPDATE_SLIDE_STATUS;
export const LIST = MODULE + _LIST;
export const FIND = MODULE + _FIND;
export const TOTAL = MODULE + _TOTAL;
export const LOAD_SLIDES = MODULE + _LOAD_SLIDES;
export const SLIDES = MODULE + _SLIDES;
export const SET_SLIDES = MODULE + _SET_SLIDES;
export const LOADING = MODULE + _LOADING;
export const ADD_SLIDE = MODULE + _ADD_SLIDE;
export const DELETE_SLIDE = MODULE + _DELETE_SLIDE;
export const REMOVE_SLIDE = MODULE + _REMOVE_SLIDE;
export const ADD_SLIDE_TO_PROJECT = MODULE + _ADD_SLIDE_TO_PROJECT;

const state = {
  slides: [],
  rawSlides: {},
  loading: false,
  byId: {},
  allIds: undefined,
  loaded: false
};

const getters = {
  [_SLIDES]: state => {
    return state.slides;
  },
  [_TOTAL](state) {
    return state.rawSlides.count;
  },
  [_FIND](state) {
    return id => state.byId[id];
  },
  [_LIST](state, getters) {
    if (state.allIds === undefined) return undefined;
    return state.allIds.map(id => getters[_FIND](id)).filter(e => e);
  }
};

const mutations = {
  add: (state, item) => {
    if (state.allIds === undefined) state.allIds = [];
    for (let key of Object.keys(item)) {
      Vue.set(state.byId, key, item[key]);
      if (state.allIds.includes(key)) return;
      state.allIds.push(key);
    }
  },
  reset: state => {
    state.allIds = [];
    state.byId = {};
  },

  [_SET_SLIDES](state, { slides }) {
    state.slides = slides;
  },
  [_ADD_SLIDE](state, { slide }) {
    state.slides.push(slide);
  },
  [_REMOVE_SLIDE](state, { slide }) {
    const predicate = s => s.id !== slide.id;
    state.slides = state.slides.filter(predicate);
    state.rawSlides.results = state.rawSlides.results.filter(predicate);
    state.rawSlides.count--;
    // remove id
    state.allIds = state.allIds.filter(x => +x.id !== slide.id);
    delete state.byId[slide.id];
  },
  [_SET_RAW_SLIDES](state, { rawSlides }) {
    state.rawSlides = rawSlides;
  },
  [_TOGGLE_LOADING](state) {
    state.loading = !state.loading;
  },
  [_RESET_SLIDES](state) {
    state.slides = [];
    state.byId = {};
    state.allIds = [];
  },
  updateSlideStatus: (state, { slide, statusId }) => {
    state.byId[slide.id].wholeslideimagestatus_set.push(statusId);
  }
};

const actions = {
  async [_LOAD_SLIDES]({ commit, state, rootGetters, rootState }, paging) {
    // if (state.loaded) return;
    // do not load again if it was already loded
    if (!paging && state.allIds && state.allIds.length > 0) {
      return;
    }
    commit(_RESET_SLIDES);
    commit(_TOGGLE_LOADING);
    const rawSlides = await getSlides(
      rootGetters.token,
      rootState.project.currentProject,
      paging
    );
    commit(_SET_RAW_SLIDES, { rawSlides });

    for (let s of state.rawSlides.results) {
      const imagingStudy = await fetchResourceWithIdentifier(
        "ImagingStudy",
        s.fhir_identifier
      );
      if (imagingStudy) {
        s.imagingStudy = prepareImagingStudy(imagingStudy);

        const flat = normalize(s, Slide);
        commit("add", flat.entities.slide);
        commit("fhir/add", imagingStudy, { root: true });
        if (flat.entities.wsiStatus !== undefined)
          commit("status/add", flat.entities.wsiStatus, { root: true });
      }
    }
    commit(_TOGGLE_LOADING);
  },
  async [_DELETE_SLIDE]({ commit, rootState, rootGetters }, slides) {
    commit(_TOGGLE_LOADING);
    const token = rootGetters.token;
    const project = rootState.project.currentProject;

    for (let slide of slides) {
      await deleteWholeSlideImage(token, project, slide.id);
      commit(_REMOVE_SLIDE, { slide });
    }
    commit(_TOGGLE_LOADING);
  },
  async [_ADD_SLIDE_TO_PROJECT]({ commit, rootGetters, rootState }, slide) {
    const token = rootGetters.token;
    const project = rootState.project.currentProject;
    const wsi = await addWholeSlideImageToProject(
      token,
      identifierToString(slide),
      project
    );
    wsi.imagingStudy = slide;
    const flat = normalize(wsi, Slide);
    commit("add", flat.entities.slide);
    commit(
      "fhir/add",
      flat.entities.imagingStudy[flat.entities.slide[flat.result].imagingStudy],
      { root: true }
    );
  },
  async [_UPDATE_SLIDE_STATUS](
    { commit, getters, rootGetters, rootState },
    { slide, newStatus, annotationClass }
  ) {
    const token = rootGetters.token;
    const oldStatus = rootGetters["status/FIND"](annotationClass.status);
    let res = await updateSlideStatus(
      token,
      newStatus,
      oldStatus,
      annotationClass,
      slide
    );
    const json = await res.json();
    const flat = normalize(json, WSIStatus);
    commit("status/add", flat.entities.wsiStatus, { root: true });
    commit("updateSlideStatus", { slide, statusId: flat.result });
    return json;
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
